import { FormatConstant, KeyConstant, SystemConstant } from "const";
import { getInteractor } from "services/local.service";
import { convertString2JSON, isJSONString, StorageUtil, toCamel } from "utils";
import { findLastIndex, sortBy, uniqBy, max } from "lodash";
import differenceInCalendarDays from "date-fns/differenceInCalendarDays";
import { convertMillisecondsToDate } from "utils/date.utils";
import store, { ConversationActions } from "redux-store";
import { getCommonLang } from "utils/lang.utils";

export const PERSONAL_DISPLAY_CALL_STATUS = [
  SystemConstant.MESSAGE_CALL_STATUS.end,
  SystemConstant.MESSAGE_CALL_STATUS.missed,
  SystemConstant.MESSAGE_CALL_STATUS.reject,
];

export const handleNewMessageList = async (newMessageList, currentMsgList, selectedGroupId, otherProps = {}) => {
  const isValid = Array.isArray(newMessageList) && newMessageList.length > 0;
  if (!isValid) return; // Do nothing if data is invalid

  const { selectedThreadId, isLoadMore } = otherProps;
  const currentMessageIds = currentMsgList.map(item => item.id);
  const messageList = isLoadMore
    ? newMessageList.filter(
        item =>
          false === Boolean(item.isDeleteOnlyMe) ||
          (Boolean(item.isDeleteOnlyMe) && currentMessageIds.includes(item.id)),
      )
    : newMessageList;

  const categoryId = selectedThreadId || selectedGroupId; // categoryId is key to get message list in group/ thread

  let handlingMessages = [...currentMsgList];
  const messageIds = [];
  const sendAsDirectMsgIds = [];
  const sortNewMessageList = sortBy(messageList, "created");

  for (let index = 0; index < sortNewMessageList.length; index++) {
    const newMessage = sortNewMessageList[index];
    handlingMessages = await handleNewMessage(newMessage, handlingMessages, selectedGroupId);
    messageIds.push(newMessage.id);
    const { send_as_direct_message } = convertString2JSON(newMessage.options, {}) || {};
    if (send_as_direct_message === SystemConstant.THREAD_MSG_SEND_OPTION.sendAsDirect) {
      sendAsDirectMsgIds.push(newMessage.id);
    }
  }

  // Remove all handling message in redux
  store.dispatch(ConversationActions.clearNewMessage(categoryId, messageIds));
  if (sendAsDirectMsgIds.length > 0)
    store.dispatch(ConversationActions.clearNewMessage(selectedGroupId, sendAsDirectMsgIds));

  return uniqBy(sortBy(handlingMessages, "created").reverse(), "id");
};

/**
 *
 * @param {JSON} newMessage: Incoming message that includes local message or remote message
 * @param {Array<JSON>} messageList: this contains calling message and others.
 * 1. About calling message, it contains origin message and children message.
 * 2. Others, it just only be latest message
 * @param {string} selectedGroupId: Selected group Id
 */
const handleNewMessage = async (newMessage, messageList, selectedGroupId) => {
  const isValid = newMessage && newMessage.id && selectedGroupId;
  if (!isValid) return; // Do nothing if data is invalid

  let newMessageList = [...messageList];
  const existedIndex = newMessageList.findIndex(
    item => newMessage.id === item.id || newMessage.sourceId === item.sourceId,
  );
  const existedParentIndex = newMessageList.findIndex(item => item.sourceId === newMessage.parentId);
  const existedParentThreadIndex = newMessageList.findIndex(item => item.sourceId === newMessage.threadId);

  const isDeleteAll = isDeletedMessageAll(newMessage);
  const isDeleteOnlyMe = isDeletedMessageOnlyMe(newMessage);
  const isSendAsDirectMessage = checkSendAsDirectMessage(newMessage);

  let oldMessage = newMessageList[existedIndex];
  switch (true) {
    case isDeleteOnlyMe:
      // Message is deleted only me in local
      const filterMessages = newMessageList.map(item => {
        const isDeleteOnlyMeMessage = [item.sourceId, item.threadId, item.parentId].includes(newMessage.parentId);
        if (isDeleteOnlyMeMessage) {
          return { ...item, state: SystemConstant.STATE.inactive, isDeleteOnlyMe: true };
        }
        return item;
      });
      newMessageList = refactorMessageList(filterMessages);
      break;

    case existedIndex >= 0:
      // 1. Overwrite message in view list or delete local db
      if (isDeleteAll) {
        newMessageList[existedIndex] = {
          ...oldMessage,
          ...newMessage,
          isDeleteAll: true,
        };
      } else if (
        max([newMessage.created, newMessage.modified, 0]) > max([oldMessage.created, oldMessage.modified, 0])
      ) {
        const newExistedMessage = await refactorMessage(
          {
            ...oldMessage,
            ...newMessage,
          },
          newMessageList[existedIndex + 1],
        );
        newMessageList[existedIndex] = newExistedMessage;
      }
      break;

    case existedParentIndex >= 0:
      // 2. New message is child message: reaction, edit, delete, calling .v.v
      oldMessage = newMessageList[existedParentIndex];
      if (SystemConstant.ARR_CALLING_TYPES.includes(newMessage.sendType)) {
        newMessageList = await getDisplayMessage(newMessageList.concat(newMessage), selectedGroupId);
      } else if (isDeleteAll) {
        newMessageList[existedParentIndex] = {
          ...oldMessage,
          isDeleteAll: true,
        };
      } else if (
        max([newMessage.created, newMessage.modified, 0]) > max([oldMessage.created, oldMessage.modified, 0])
      ) {
        newMessageList[existedParentIndex] = {
          ...oldMessage,
          modified: newMessage.modified || newMessage.created,
        };
      }

      break;

    case isSendAsDirectMessage:
      if (existedParentThreadIndex >= 0) {
        oldMessage = newMessageList[existedParentThreadIndex];
        if (oldMessage.state === SystemConstant.STATE.active) {
          newMessageList[existedParentThreadIndex] = {
            ...oldMessage,
            modified: newMessage.modified || newMessage.created,
          };
        }
      }

      if (false === Boolean(newMessage.parentId)) {
        const refactorMsg = await refactorMessage(newMessage, newMessageList[0]);
        newMessageList.unshift(refactorMsg);
      }

      break;

    case existedParentThreadIndex >= 0:
      oldMessage = newMessageList[existedParentThreadIndex];
      newMessageList[existedParentThreadIndex] = {
        ...oldMessage,
        modified: newMessage.modified || newMessage.created,
      };
      break;

    case newMessage.isDeleteOnlyMe:
      // Receive delete_only_me message from server
      newMessageList = await getDisplayMessage(newMessageList.concat(newMessage), selectedGroupId);
      break;

    case SystemConstant.ARR_CALLING_TYPES.includes(newMessage.sendType):
      // Add new calling message
      newMessageList = await getDisplayMessage(newMessageList.concat(newMessage), selectedGroupId);
      break;

    case ![...SystemConstant.ARR_CALLING_TYPES, SystemConstant.SEND_TYPE.reaction].includes(newMessage.sendType) &&
      false === Boolean(newMessage.parentId):
      // Add new message
      const displayMsgItem = await refactorMessage(newMessage, newMessageList[0]);
      newMessageList.unshift(displayMsgItem);
      break;

    default:
      break;
  }

  return newMessageList;
};

export const getDisplayMessage = async (messages, selectedGroupId) => {
  // Skip if data is invalid
  if (!Boolean(selectedGroupId)) return [];

  const prefixKey = StorageUtil.getCurrentPrefixKey();
  const groupDetail = await getInteractor(prefixKey).LocalGroupService.get(selectedGroupId);
  const { callingMessageList, otherMessages } = messages.reduce(
    (accumulator, messageItem) => {
      const isCalling = SystemConstant.ARR_CALLING_TYPES.includes(messageItem.sendType);
      if (isCalling) {
        accumulator.callingMessageList.push(messageItem);
      } else {
        accumulator.otherMessages.push(messageItem);
      }

      return accumulator;
    },
    { callingMessageList: [], otherMessages: [] },
  );

  // Calling messages
  const callingList =
    callingMessageList.length > 0
      ? await getDisplayCallingMsg(
          callingMessageList,
          groupDetail.groupType === SystemConstant.GROUP_CHAT_TYPE.personal,
        )
      : [];

  // Remove duplicate
  let showingList = uniqBy(otherMessages.concat(callingList), "id");
  // Sort by created
  showingList = sortBy(showingList, "created");

  const refactorList = await refactorMessageList(showingList);
  return refactorList.reverse();
};

const getDisplayCallingMsg = async (callingMessages, isPersonal) => {
  const prefixKey = StorageUtil.getCurrentPrefixKey();
  let displayCallingList = [];

  try {
    if (isPersonal) {
      displayCallingList = await callingMessages.reduce(async (previousPromise, message) => {
        const messageList = await previousPromise;

        const parentId = message.parentId || message.sourceId;
        const childMessageList = await getInteractor(prefixKey).LocalMessageService.getChildMessages(
          parentId,
          SystemConstant.ARR_CALLING_TYPES,
        );
        const lastChildMessage = toCamel(childMessageList[0]);

        if (lastChildMessage && PERSONAL_DISPLAY_CALL_STATUS.includes(lastChildMessage.callStatus)) {
          messageList.push(toCamel(lastChildMessage));
        }

        return messageList;
      }, Promise.resolve([]));
    } else {
      const messageIds = [];
      const originCallingMsgs = (
        await Promise.all(
          callingMessages.map(async message => {
            const originMessage = isChildMessage(message)
              ? await getInteractor().LocalMessageService.get(message.parentId)
              : message;

            return toCamel(originMessage);
          }),
        )
      ).filter(message => {
        if (!message?.id || messageIds.includes(message.id)) return false;

        messageIds.push(message.id);
        return true;
      });

      displayCallingList = await originCallingMsgs.reduce(async (previousPromise, message) => {
        const messageList = await previousPromise;

        const childMessageList = await getInteractor().LocalMessageService.getChildMessages(
          message.sourceId,
          SystemConstant.ARR_CALLING_TYPES,
        );

        // Remove duplicate ended calling
        const removeDuplicateEndCalling = toCamel([message, ...childMessageList]).filter((message, index) => {
          const lastIndex = findLastIndex(
            displayCallingList,
            last => last.parentId === message.parentId && last.callStatus === message.callStatus,
          );
          const isDuplicateEndMessage =
            lastIndex !== index && message.callStatus === SystemConstant.MESSAGE_CALL_STATUS.end;

          return false === isDuplicateEndMessage;
        });

        return messageList.concat(toCamel(removeDuplicateEndCalling));
      }, Promise.resolve([]));
    }
  } catch (error) {
    console.error(error);
  }

  // Remove Delete message Only me with calling_message
  const removeSourceIds = [];
  const returnCallingMessages = [];
  for (let index = 0; index < displayCallingList.length; index++) {
    const message = displayCallingList[index];
    const parentId = message.parentId || message.sourceId;

    // Next if parentId is in remove list
    if (removeSourceIds.includes(parentId)) {
      continue;
    }

    // Check active calling message and delete only me message
    const [inactiveCallingCount, deletedOnlyMeCount] = await Promise.all([
      getInteractor(prefixKey).LocalCallHistoryService.count({
        state: SystemConstant.STATE.inactive,
        source_id: parentId,
      }),
      getInteractor(prefixKey).LocalMessageService.count({
        state: SystemConstant.STATE.active,
        parent_id: parentId,
        send_type: SystemConstant.SEND_TYPE.deleteMessageOnlyMe,
      }),
    ]);

    const isActiveCallingMsg = inactiveCallingCount + deletedOnlyMeCount === 0;
    if (isActiveCallingMsg) {
      returnCallingMessages.push(message);
    } else {
      removeSourceIds.push(parentId);
    }
  }

  return returnCallingMessages;
};

export const refactorMessage = async (messageItem, previousMessage = null) => {
  const prefixKey = StorageUtil.getCurrentPrefixKey();
  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
  const branchId = StorageUtil.getItem(KeyConstant.KEY_BRANCH_ID, prefixKey);
  messageItem = toCamel(messageItem);

  // Check message can be display avatar and timeline
  let isAvatar = false;
  let isShowTime =
    Boolean(previousMessage && previousMessage.state === SystemConstant.STATE.inactive && previousMessage.isShowTime) ||
    Boolean(!previousMessage?.created && !messageItem.threadId);
  const diffDays =
    messageItem.created && previousMessage?.created
      ? differenceInCalendarDays(new Date(messageItem.created), new Date(previousMessage.created))
      : 0;
  isShowTime = isShowTime || diffDays > 0;

  if (messageItem.senderId !== accountId) {
    const isDiffMessageType =
      previousMessage &&
      SystemConstant.ARR_CALLING_TYPES.concat(SystemConstant.MESSAGE_NOTIFY).includes(previousMessage.sendType) &&
      !SystemConstant.ARR_CALLING_TYPES.concat(SystemConstant.MESSAGE_NOTIFY).includes(messageItem.sendType);

    const messageOptions = messageItem.options ? toCamel(convertString2JSON(messageItem.options)) : {};
    const previousOptions = previousMessage ? toCamel(convertString2JSON(previousMessage.options)) : {};
    const isDiffDisappear = false === Boolean(previousOptions.disappearingF) && Boolean(messageOptions.disappearingF);
    const isNotFoundMessage = messageItem.sendType === SystemConstant.SEND_TYPE.notFound;
    const isPreviousAvatar =
      previousMessage && previousMessage.state === SystemConstant.STATE.inactive && previousMessage.isAvatar;

    isAvatar =
      isNotFoundMessage ||
      (!SystemConstant.ARR_CALLING_TYPES.includes(messageItem.sendType) &&
        (isShowTime ||
          !previousMessage ||
          isDiffMessageType ||
          previousMessage.senderId !== messageItem.senderId ||
          isDiffDisappear ||
          isPreviousAvatar));
  }

  const senderAccount = toCamel(
    (await getInteractor(prefixKey).LocalAccountService.get(messageItem.senderId, branchId)) || {},
  );

  if (messageItem.sendType === SystemConstant.SEND_TYPE.quote && isJSONString(messageItem.content)) {
    const messageContentObj = convertString2JSON(messageItem.content, {});
    messageItem.content = messageContentObj.content_message;
    messageItem.quoteId = messageContentObj.quote_id;
  }

  let isDeletedMsg = false;
  const totalDeletingChildMsg = await getInteractor(prefixKey).LocalMessageService.count({
    parent_id: messageItem.sourceId,
    send_type: SystemConstant.SEND_TYPE.deleteMessage,
  });
  isDeletedMsg = totalDeletingChildMsg > 0;

  // Check delete only me
  let isDeleteOnlyMe = Boolean(messageItem.isDeleteOnlyMe);
  if (!isDeleteOnlyMe) {
    const totalDeleteOnlyMe = await getInteractor(prefixKey).LocalMessageService.count({
      parent_id: messageItem.sourceId,
      send_type: SystemConstant.SEND_TYPE.deleteMessageOnlyMe,
    });
    isDeleteOnlyMe = totalDeleteOnlyMe > 0;
  }

  const sendAsDirectMessage = checkSendAsDirectMessage(messageItem);

  return {
    ...messageItem,
    isDeleteAll: isDeletedMsg,
    isDeleteOnlyMe,
    isShowTime: isShowTime,
    isAvatar: isAvatar,
    avatarId: senderAccount.avatarId,
    avatarUrl: senderAccount.avatarUrl,
    senderName: senderAccount.name || getCommonLang("TXT_USER_TRIOS"),
    sentTime: convertMillisecondsToDate(messageItem.created, FormatConstant.FM_HH_MM),
    sendAsDirectMessage,
  };
};

export const refactorMessageList = async messages => {
  const messageList = messages.filter(messageItem => messageItem && messageItem.id);

  try {
    const rewriteMessageList = [];

    for (let index = 0; index < messageList.length; index++) {
      const messageItem = await refactorMessage(messageList[index], rewriteMessageList[rewriteMessageList.length - 1]);
      rewriteMessageList.push(messageItem);
    }

    return rewriteMessageList;
  } catch (error) {
    console.error(error);
  }

  return messageList;
};

export const handleDisappearMsg = (currentMessageList, disappearIds) => {
  const newMessageList = [...currentMessageList];

  for (let index = 0; index < disappearIds.length; index++) {
    const disappearId = disappearIds[index];
    const disappearIndex = currentMessageList.findIndex(item => item.id === disappearId);
    const disappearMessage = newMessageList[disappearIndex];
    const nextMessage = currentMessageList[disappearIndex - 1];

    if (disappearIndex >= 0 && nextMessage && nextMessage.senderId === disappearMessage.senderId) {
      newMessageList[disappearIndex - 1] = {
        ...nextMessage,
        isShowTime: disappearMessage.isShowTime,
        isAvatar: disappearMessage.isAvatar,
      };
    }
  }

  const filterList = newMessageList.filter(item => false === Boolean(disappearIds.includes(item.id)));
  return filterList;
};

const isChildMessage = mes => Boolean(mes.parentId);

const isDeletedMessageAll = messageItem => {
  return messageItem.sendType === SystemConstant.SEND_TYPE.deleteMessage;
};

const isDeletedMessageOnlyMe = messageItem => {
  return messageItem.sendType === SystemConstant.SEND_TYPE.deleteMessageOnlyMe;
};

export const CHAT_WRAPPER_ID = "conversation-id";
export const scrollToTopInbox = () => {
  const wrapper = document.getElementById(CHAT_WRAPPER_ID);
  if (wrapper) wrapper.scrollTop = wrapper.scrollHeight;

  store.dispatch(
    ConversationActions.conversationSet(
      {
        scrollToChildId: null,
      },
      StorageUtil.getCurrentPrefixKey(),
    ),
  );
};

export const isLocalMessage = newestMessage => {
  const camelMessage = toCamel(newestMessage);
  return (
    camelMessage.senderDeviceId === StorageUtil.getItem(KeyConstant.KEY_DEVICE_ID) &&
    !newestMessage.isDeleteOnlyMe &&
    !camelMessage.parentId &&
    [
      SystemConstant.SEND_TYPE.message,
      SystemConstant.SEND_TYPE.groupMessage,
      SystemConstant.SEND_TYPE.link,
      ...SystemConstant.ARR_CALLING_TYPES,
      ...Object.values(SystemConstant.MEDIA_SEND_TYPE),
      SystemConstant.SEND_TYPE.quote,
    ].includes(camelMessage.sendType)
  );
};

const checkSendAsDirectMessage = message => {
  const messageOptions = message.options ? convertString2JSON(message.options, {}) : {};
  const { send_as_direct_message } = messageOptions;
  return send_as_direct_message === SystemConstant.THREAD_MSG_SEND_OPTION.sendAsDirect;
};
